import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import map from 'lodash/map'
import some from 'lodash/some'
import { useSelector, useDispatch } from 'react-redux'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import WorkspaceSelectionTable from '@SiteComponents/WorkspaceSelectionTable'
import Popup from '@SiteComponents/Popup'
import Button from '@SiteComponents/Button'
import Close from '@Images/close_create_workspace_popup.svg'
import Alert from '@Images/project_setup_form_alert.svg'
import ArrowDown from '@Images/create_account_user_arrow_down.svg'
import Checkmark from '@Images/guidelines_dropdown_checked.svg'

import {
  closeCreateAccountUserPopup,
} from '@SiteModules/Popup/actions'

import {
  createAccountUsers,
} from '@SiteModules/AccountUser/actions'

import {
  getCurrentAccountId,
  getCurrentAccountMaxNumberOfAccountUsers,
} from '@SiteModules/Account/selectors'

import {
  getAccountUsers,
} from '@SiteModules/AccountUser/selectors'

import {
  getWorkspaces,
} from '@SiteModules/Workspace/selectors'

import {
sanitizeEmail,
isValidEmail,
} from '@Utils/EmailSanitizerAndValidator'

const DROPDOWN_WIDTH = 250 // px, as in CreateAccountUserPopup.scss
const DROPDOWN_WRAPPER_RIGHT = 8 // px, as in CreateAccountUserPopup.scss

import './CreateAccountUserPopup.scss'

// Dimensions from // used in ManageWorkspaceAccessPopup.scss
const CREATE_ACCOUNT_USER_POPUP_PADDING_TOP = 24 // px
const CREATE_ACCOUNT_USER_POPUP_PADDING_BOTTOM = 24 // px
const CREATE_ACCOUNT_USER_POPUP_HEADER_HEIGHT = 24 // px
const CREATE_ACCOUNT_USER_POPUP_HEADER_MARGIN_BOTTOM = 32 // px
const CREATE_ACCOUNT_USER_POPUP_WORKSPACE_SELECTION_MARGIN_TOP = 42 // px
const CREATE_ACCOUNT_USER_POPUP_BUTTON_WRAPPER_HEIGHT = 32 // px
const CREATE_ACCOUNT_USER_POPUP_BUTTON_WRAPPER_MARGIN_TOP = 32 // px

const REMAINING_POPUP_HEIGHT_BESIDES_TABLE_AND_INPUT_WRAPPER = CREATE_ACCOUNT_USER_POPUP_PADDING_TOP +
  CREATE_ACCOUNT_USER_POPUP_PADDING_BOTTOM +
  2 * CREATE_ACCOUNT_USER_POPUP_HEADER_HEIGHT +
  2 * CREATE_ACCOUNT_USER_POPUP_HEADER_MARGIN_BOTTOM +
  CREATE_ACCOUNT_USER_POPUP_WORKSPACE_SELECTION_MARGIN_TOP +
  CREATE_ACCOUNT_USER_POPUP_BUTTON_WRAPPER_HEIGHT +
  CREATE_ACCOUNT_USER_POPUP_BUTTON_WRAPPER_MARGIN_TOP

const CreateAccountUserPopup: React.FC = () => {
  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [role, setRole] = useState('Member')
  const [showRoleDropdown, setShowRoleDropdown] = useState(false)
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<number[]>([])
  const [inputWrapperHeight, setInputWrapperHeight] = useState(0)

  const currentAccountId = useSelector(getCurrentAccountId)
  const accountUsers = useSelector(getAccountUsers)
  const currentAccountMaxNumberOfAccountUsers = useSelector(getCurrentAccountMaxNumberOfAccountUsers)
  const workspaces = useSelector(getWorkspaces)

  const roleButtonRef = useRef<HTMLDivElement | null>(null)

  useClickOutsideListener('CreateAccountUserPopup__role-dropdown-element', setShowRoleDropdown, showRoleDropdown)

  const popupClassName='CreateAccountUserPopup'
  const inputWrapperClassName = 'CreateAccountUserPopup__input-wrapper'
  const hasNoEmails = emails.length === 0
  const hasAnUnfinishedEmail = sanitizeEmail(inputValue).length > 0

  const sanitizedEmails = map(emails, e => sanitizeEmail(e))
  const hasInvalidEmails = some(sanitizedEmails, e => !isValidEmail(e))
  const hasAtLeastOneValidEmail = some(sanitizedEmails, e => isValidEmail(e))
  const numberOfAccountUsersThatCanBeAdded = currentAccountMaxNumberOfAccountUsers - accountUsers.length
  const tooManyAccountUsers = emails.length > numberOfAccountUsersThatCanBeAdded
  const memberSelected = role === 'Member'
  const adminSelected = role === 'Admin'

  const shouldRenderWorkspaceSelection = memberSelected && workspaces.length > 0 && hasAtLeastOneValidEmail
  const shouldBlockCreateUserButton = hasNoEmails || hasAnUnfinishedEmail || hasInvalidEmails || tooManyAccountUsers

  useEffect(() => {
    calculateAndSetInputWrapperHeight()
  }, [])

  useEffect(() => {
    calculateAndSetInputWrapperHeight()
  }, [emails.length])

  useEffect(() => {
    if (!shouldRenderWorkspaceSelection) {
      setSelectedWorkspaceIds([])
    }
  }, [shouldRenderWorkspaceSelection])

  const calculateAndSetInputWrapperHeight = () => {
    const el = document.getElementsByClassName(inputWrapperClassName)[0] as HTMLElement | undefined

    if (el) {
      const heightOfInputWrapper = el.offsetHeight
      setInputWrapperHeight(heightOfInputWrapper)
    }
  }

  const toggleRoleDropdown = () => {
    setShowRoleDropdown(!showRoleDropdown)
  }

  const setAdmin = () => {
    if (adminSelected) { return }

    setRole('Admin')
  }

  const setMember = () => {
    if (memberSelected) { return }

    setRole('Member')
  }

  const handleCancel = () => {
    dispatch(closeCreateAccountUserPopup())
  }

  const handleCreate = () => {
    dispatch(
      createAccountUsers({
        emails: sanitizedEmails,
        accountId: currentAccountId,
        role: role.toLowerCase(),
        ...(selectedWorkspaceIds.length > 0 ? { workspaceIds: selectedWorkspaceIds } : {}),
      })
    )
    dispatch(closeCreateAccountUserPopup())
  }

  const addEmail = (rawEmail: string) => {
    const email = sanitizeEmail(rawEmail)
    if (email && !emails.includes(email)) {
      setEmails([...emails, email])
    }
  }

  const changeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value.includes(',')) {
      const parts = value.split(',')
      const lastPart = parts.pop() || ''
      parts.forEach(addEmail)
      setInputValue(lastPart)
    } else {
      setInputValue(value)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      handleInputValue()
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      handleInputValue()
    }
  }

  const handleInputValue = () => {
    addEmail(inputValue.trim())
    setInputValue('')
  }


  const handleRemoveEmail = (e: React.MouseEvent<HTMLSpanElement>) => {
    const emailToRemove = e.currentTarget.getAttribute('data-email')
    if (emailToRemove) {
      setEmails(emails.filter(email => email !== emailToRemove))
    }
  }

  const renderButtons = () => {
    return (
      <div className="CreateAccountUserPopup__button-wrapper">
        <Button
          styleType="gray-subtle"
          size="small"
          caption="Cancel"
          onClick={handleCancel}
          icon={<Close/>}
        />
        <Button
          styleType="brand"
          size="small"
          caption="Create User"
          onClick={handleCreate}
          disabled={shouldBlockCreateUserButton}
        />
      </div>
    )
  }

  const renderAccountUserNameInput = () => {
    return (
      <div className="CreateAccountUserPopup__input-wrapper">
        <div className="CreateAccountUserPopup__tag-input">
          {map(emails, email => {
            const isInvalid = !isValidEmail(email)
            return (
              <div
                key={email}
                className={`CreateAccountUserPopup__tag${isInvalid ? ' CreateAccountUserPopup__tag--invalid' : ''}`}
              >
                {email}
                <span
                  className="CreateAccountUserPopup__tag-remove"
                  data-email={email}
                  onClick={handleRemoveEmail}
                >
                  ×
                </span>
              </div>
            )
          })}
          <input
            className="CreateAccountUserPopup__input"
            value={inputValue}
            onChange={changeInput}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={hasNoEmails ? 'Enter their email addresses...' : ''}
          />
          {renderRoleDropdownButton()}
        </div>
        {hasInvalidEmails && renderAlertInvalidEmails()}
        {tooManyAccountUsers && renderAlertTooManyAccountUsers()}
      </div>
    )
  }

  const renderRoleDropdownButton = () => {
    return (
      <div className="CreateAccountUserPopup__role-dropdown-wrapper CreateAccountUserPopup__role-dropdown-element">
        <div
          className="CreateAccountUserPopup__role-dropdown"
          onClick={toggleRoleDropdown}
          ref={roleButtonRef}
        >
          <div className="CreateAccountUserPopup__role-dropdown-selected">
            {role}
          </div>
          <ArrowDown />
        </div>
        {showRoleDropdown && renderRoleDropdown()}
      </div>
    )
  }

  const renderRoleDropdown = () => {
    const roleButtonBounds = roleButtonRef.current ? roleButtonRef.current.getBoundingClientRect() : { x: 0, y: 0, width: 0, height: 0 }
    const dropdownY = roleButtonBounds.y + roleButtonBounds.height
    const dropdownX = roleButtonBounds.x - DROPDOWN_WIDTH + roleButtonBounds.width + DROPDOWN_WRAPPER_RIGHT

    const style = {
      top: `${dropdownY}px`,
      left: `${dropdownX}px`
    }

    return ReactDOM.createPortal(
      (
        <div
          className="CreateAccountUserPopup__role-dropdown-items-wrapper CreateAccountUserPopup__role-dropdown-element"
          style={style}
        >
          <ul className="CreateAccountUserPopup__role-dropdown-items">
            <li
              className={classnames(
                'CreateAccountUserPopup__role-dropdown-item',
                { 'CreateAccountUserPopup__role-dropdown-item-unselected': memberSelected }
              )}
              onClick={setAdmin}
            >
              <div className="CreateAccountUserPopup__role-dropdown-item-header">
                Admin
              </div>
              <div className="CreateAccountUserPopup__role-dropdown-item-description">
                They have full access to <br />
                view and edit everything.
              </div>
              {adminSelected && (
                <Checkmark />
              )}
            </li>
            <li
              className={classnames(
                'CreateAccountUserPopup__role-dropdown-item',
                { 'CreateAccountUserPopup__role-dropdown-item-unselected': adminSelected }
              )}
              onClick={setMember}
            >
              <div className="CreateAccountUserPopup__role-dropdown-item-header">
                Member
              </div>
              <div className="CreateAccountUserPopup__role-dropdown-item-description">
                They can view public content <br />
                and what's shared with them.
              </div>
              {memberSelected && (
                <Checkmark />
              )}
            </li>
          </ul>
        </div>
      ),
      document.getElementById('site-container')!
    )
  }

  const renderAlertInvalidEmails = () => {
    return (
      <div className="CreateAccountUserPopup__alert">
        <Alert />
        Invalid email address
      </div>
    )
  }

  const renderAlertTooManyAccountUsers = () => {
    return (
      <div className="CreateAccountUserPopup__alert">
        <Alert />
        <div>
          You’ve reached the maximum number of users allowed in your plan. Please{' '}
          <a href="mailto:team+support@checksub.com" className="CreateAccountUserPopup__link">
            contact us
          </a>{' '}
          to review your subscription.
        </div>
      </div>
    )
  }

  const renderWorkspaceSelection = () => {
    return (
      <div className="CreateAccountUserPopup__workspace-selection">
        <div className="CreateAccountUserPopup__header">
          Assign to workspaces
        </div>
        <WorkspaceSelectionTable
          workspaces={workspaces}
          selectedWorkspaceIds={selectedWorkspaceIds}
          setSelectedWorkspaceIds={setSelectedWorkspaceIds}
          popupClassName={popupClassName}
          remainingPopupHeight={REMAINING_POPUP_HEIGHT_BESIDES_TABLE_AND_INPUT_WRAPPER + inputWrapperHeight}
        />
      </div>
    )
  }

  return (
    <Popup
      className={popupClassName}
      closePopup={handleCancel}
    >
      <div className="CreateAccountUserPopup__header">
        Invite users to join your account
      </div>
      {renderAccountUserNameInput()}
      {shouldRenderWorkspaceSelection && renderWorkspaceSelection()}
      {renderButtons()}
    </Popup>
  )
}

export default CreateAccountUserPopup
